// The Team screen's server calls outside the sync feed (WEB_PLAN.md §5.4): `team_roster` through TanStack Query
// (refetched on the shell's Refresh and after every change made here), and the admin RPCs `invite_member`,
// `revoke_invitation` and `remove_member`; also the invite flow wired to this session's engine (invite.ts).

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useSession } from '@/auth/AuthProvider';
import { callRpc } from '@/auth/rpc';
import type { Roster, TeamInvitation } from '@/contracts/wire';
import { REFRESH_EVENT } from '@/features/shell/refresh';
import { useSyncEngine } from '@/sync/hooks';
import { engineServerCopySource, waitForServerCopy, type InviteDeps, type InviteMemberParams } from './invite';
import { normalizeInvitation, normalizeRoster } from './members';

export const rosterQueryKey = (companyId: string) => ['team_roster', companyId.toLowerCase()] as const;

export interface RosterQuery {
  /** Null until loaded (and while it fails). */
  roster: Roster | null;
  loading: boolean;
  error: unknown;
  /** Refetch now (Retry, and after an invite, revoke or removal). */
  refresh: () => void;
}

/** `team_roster` for the session's company. Only admins and managers may read it (`enabled`). */
export function useTeamRoster(enabled: boolean): RosterQuery {
  const { companyId } = useSession();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => rosterQueryKey(companyId), [companyId]);
  const query = useQuery({
    queryKey,
    queryFn: async () => normalizeRoster(await callRpc<unknown>('team_roster', { p_company: companyId.toLowerCase() })),
    enabled,
    staleTime: 30_000,
  });
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);
  // The shell's Refresh button pulls the feed itself and broadcasts this for server queries like ours.
  useEffect(() => {
    if (!enabled) return;
    window.addEventListener(REFRESH_EVENT, refresh);
    return () => window.removeEventListener(REFRESH_EVENT, refresh);
  }, [enabled, refresh]);
  return {
    roster: query.data ?? null,
    loading: enabled && query.isPending,
    error: query.error,
    refresh,
  };
}

export async function inviteMember(params: InviteMemberParams): Promise<TeamInvitation> {
  const invitation = normalizeInvitation(await callRpc<unknown>('invite_member', { ...params }));
  if (!invitation) throw new Error('Unexpected response from the server (invitation).');
  return invitation;
}

export async function revokeInvitation(invitationId: string): Promise<void> {
  await callRpc<unknown>('revoke_invitation', { p_invitation: invitationId.toLowerCase() });
}

/** Removes the account from the company; the profile and its history stay, and the person can be invited again. */
export async function removeMember(companyId: string, userId: string): Promise<void> {
  await callRpc<unknown>('remove_member', { p_company: companyId.toLowerCase(), p_user: userId.toLowerCase() });
}

/** The invite flow wired to this session's engine and RPCs. */
export function useInviteDeps(): InviteDeps {
  const engine = useSyncEngine();
  return useMemo<InviteDeps>(() => {
    const source = engineServerCopySource(engine, 'users');
    return {
      save: (patches) => engine.save(patches),
      waitForProfile: (profileId, accept) => waitForServerCopy(source, profileId, accept),
      inviteMember,
    };
  }, [engine]);
}
